import React, { useState } from 'react'
import PropTypes from 'prop-types'
import { connect } from 'react-redux'
import { Column, Columns, Container } from '@resurs/midas-react'

import InvoiceNumberSettings from '../InvoiceNumberSettings'
import CartItemMergeSettings from '../CartItemMergeSettings'
import {
  getInvoiceNumber,
  getPaymentMethodsByChannel,
  getRepresentativeDetails,
  setInvoiceNumber,
  updateOrderDateAndFee,
  getCartItemMergeParams,
  updateCartItemMergeParams,
} from '../../../store/actions/store-info-actions'
import { getText } from '../../../internal/TextLabels'
import { ChainTypes } from '../../../models/chainTypes'
import PaymentMethodSettings from '../PaymentMethodSettings'
import { useOnMount } from '../../../util/utility-hooks'
import { Features, isEnabled } from '../../../models/features'
import { CartItemMergeParamsValidRoles } from '../../../models/roles'

const PaymentSettings = ({
  country,
  currentRepresentative,
  loadingDetails,
  paymentMethods,
  invoiceNumberLoading,
  nextInvoiceNumber,
  loggedInUser,
  ...props
}) => {
  const [originalCartItemMergeParams, setOriginalCartItemMergeParams] =
    useState([])

  useOnMount(() => {
    props.getRepresentativeDetails()
    props.getInvoiceNumber()

    if (isEnabled(Features.CART_ITEM_MERGE)) {
      props.getCartItemMergeParams().then(setOriginalCartItemMergeParams)
    }
  })

  const isFranchiseOrNoRelations = () => {
    const chainType = currentRepresentative.chainType || ChainTypes.SINGLE_OWNER
    return (
      chainType === ChainTypes.FRANCHISEE ||
      chainType === ChainTypes.NO_RELATIONS
    )
  }

  return (
    <Container grid960>
      <Columns>
        <Column col1 colMd0 />
        <Column col10 colMd12>
          <Container>
            <h4>{getText('paymentSettings.header')}</h4>
            <div>{getText('paymentSettings.description')}</div>
          </Container>
          <PaymentMethodSettings
            isFranchiseOrNoRelations={isFranchiseOrNoRelations()}
            updateOrderDateAndFee={props.updateOrderDateAndFee}
            paymentMethods={paymentMethods}
            getPaymentMethodsByChannel={channel =>
              props.getPaymentMethodsByChannel(channel)
            }
          />
          <InvoiceNumberSettings
            loadingInvoiceNumber={loadingDetails || invoiceNumberLoading}
            nextInvoiceNumber={nextInvoiceNumber}
            updateInvoiceNumber={props.setInvoiceNumber}
          />
          {isEnabled(Features.CART_ITEM_MERGE) &&
            CartItemMergeParamsValidRoles.includes(loggedInUser.role) && (
              <CartItemMergeSettings
                parameters={originalCartItemMergeParams}
                onSave={newParams => props.updateCartItemMergeParams(newParams)}
              />
            )}
        </Column>
      </Columns>
    </Container>
  )
}

PaymentSettings.propTypes = {
  loadingDetails: PropTypes.bool.isRequired,
  currentRepresentative: PropTypes.object,
  getRepresentativeDetails: PropTypes.func.isRequired,
  paymentMethods: PropTypes.array.isRequired,
  getPaymentMethodsByChannel: PropTypes.func.isRequired,
  updateOrderDateAndFee: PropTypes.func.isRequired,
  getInvoiceNumber: PropTypes.func.isRequired,
  setInvoiceNumber: PropTypes.func.isRequired,
  invoiceNumberLoading: PropTypes.bool.isRequired,
  nextInvoiceNumber: PropTypes.number.isRequired,
  country: PropTypes.string.isRequired,
  getCartItemMergeParams: PropTypes.func.isRequired,
  updateCartItemMergeParams: PropTypes.func.isRequired,
  loggedInUser: PropTypes.object.isRequired,
}

PaymentSettings.defaultProps = {
  currentRepresentative: null,
}

const mapStateToProps = state => ({
  loadingDetails: state.storeInfo.loadingDetails,
  currentRepresentative: state.storeInfo.currentRepresentative,
  paymentMethods: state.storeInfo.currentPaymentMethodsByChannel,
  country: state.storeInfo.currentRepresentative.country,
  invoiceNumberLoading: state.settings.invoiceNumberLoading,
  nextInvoiceNumber: state.settings.invoiceNumber,
  loggedInUser: state.auth,
})

const mapDispatchToProps = dispatch => ({
  getRepresentativeDetails: () => {
    dispatch(getRepresentativeDetails())
  },
  getPaymentMethodsByChannel: channel =>
    dispatch(getPaymentMethodsByChannel(channel)),
  updateOrderDateAndFee: (channel, requestData) => {
    dispatch(updateOrderDateAndFee(channel, requestData))
  },
  getInvoiceNumber: () => {
    dispatch(getInvoiceNumber())
  },
  setInvoiceNumber: newNumber => dispatch(setInvoiceNumber(newNumber)),
  getCartItemMergeParams: () => dispatch(getCartItemMergeParams()),
  updateCartItemMergeParams: cartItemMergeParams =>
    dispatch(updateCartItemMergeParams(cartItemMergeParams)),
})

export default connect(mapStateToProps, mapDispatchToProps)(PaymentSettings)
